import { styled } from '@mui/material/styles';
import type {
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
} from '@mui/x-data-grid-pro';
import { DataTableContext, useDataTableContext } from '@/features/DataGrid';
import { Analytics } from '@/shared/lib';
import type { ColumnsConfig } from '@/shared/types';
import type { MinMaxData, TotalData } from '@/shared/ui';
import { DataGrid as SharedDataGrid, useDataGridListeners } from '@/shared/ui';
import type { DataResponse } from '../../model/types';
import { useData } from './useData';
import { useTableProps } from './useTableProps';

const DataGrid = styled(SharedDataGrid)(({ theme }) => ({
  '& .dimensionCell + .metricCell, & .dimensionHeader + .metricHeader': {
    boxShadow: 'inset 8px 0 8px -8px #00234033',
  },

  '& .dimensionCell': {
    padding: `0 ${theme.spacing(1.25)}`,
    flexShrink: 0,
  },

  '& .columnGroup': {
    display: 'none',
  },
}));

interface Props {
  rowHeight: number;
  columnsWidth: Record<string, number | undefined>;
  columnsConfig: ColumnsConfig;
  dimensions: string[];
  metric: string;
  minMax?: MinMaxData[string];
  getRowsPage: (offset?: number) => Promise<DataResponse>;
  getTotal: () => Promise<TotalData>;
  reorderDimensions: (info: {
    id: string;
    oldIndex: number;
    newIndex: number;
  }) => void;
  updateColumnsWidth: (columnName: string, newWidth?: number) => void;
}

function Content({
  rowHeight,
  columnsWidth,
  columnsConfig,
  dimensions,
  metric,
  minMax,
  reorderDimensions,
  updateColumnsWidth,
  getRowsPage,
  getTotal,
}: Props) {
  const {
    initialState,
    detailPanelExpandedRowIds,
    getDetailPanelHeight,
    getDetailPanelContent,
  } = useDataTableContext();
  const {
    rows,
    columns: dataColumns,
    loading,
    getNextPage,
  } = useData({
    getTotal,
    getRowsPage,
  });
  const { columnGroupingModel, columns } = useTableProps({
    columnsWidth,
    columnsConfig,
    dimensions,
    metric,
    dataColumns,
    minMax,
  });

  useDataGridListeners();

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const { column } = params;
    // Need to substract 1 from indexes due to hidden row details column
    const oldIndex = params.oldIndex - 1;
    const targetIndex = params.targetIndex - 1;

    if (oldIndex === targetIndex) {
      return;
    }

    reorderDimensions({
      id: column.field,
      oldIndex: oldIndex,
      newIndex: targetIndex,
    });
    Analytics.sendFeatureUsage('data_table', 'reorder_column');
  };
  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const { width, field } = params.colDef;

    updateColumnsWidth(field, width);
  };

  return (
    <DataGrid
      rowHeight={rowHeight}
      initialState={initialState}
      loading={loading}
      detailPanelExpandedRowIds={detailPanelExpandedRowIds}
      getDetailPanelHeight={getDetailPanelHeight}
      getDetailPanelContent={getDetailPanelContent}
      columnGroupingModel={columnGroupingModel}
      columns={columns}
      rows={rows}
      getRowClassName={(params) => {
        return params.row.isTotal ? 'total' : '';
      }}
      onRowsScrollEnd={getNextPage}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
}

export function DataTable(props: Props) {
  return (
    <DataTableContext pageFetcher={props.getRowsPage}>
      <Content {...props} />
    </DataTableContext>
  );
}
